import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  casesService,
  getCaseStatsFilters,
  type Case,
  type PaginatedResponse,
  type CaseFilters,
  type CaseStats,
  type CaseStatusHistoryItem,
  type CaseFile,
  type CaseErrorReport,
  type CaseEmail,
  type CaseNote,
  type CreateCaseDto,
  type UpdateCaseDto,
  type GenerateCassetteLabelsRequest,
  type GenerateStickerLabelsRequest,
  type DashboardStats,
  type AdditionalStainInfo,
  type CaseQaReview,
  type UnifiedCommunication,
} from "../services";

export function useCases(filters: CaseFilters = {}, page = 1, limit = 50) {
  return useQuery({
    queryKey: ["cases", page, limit, filters],
    queryFn: () => casesService.getAll(filters, page, limit),
  });
}

export function useCase(id: string | undefined) {
  return useQuery({
    queryKey: ["case", id],
    queryFn: () => casesService.getById(id!),
    enabled: !!id,
  });
}

export function useCaseByCaseNumber(caseNumber: string | undefined) {
  return useQuery({
    queryKey: ["case", "by-number", caseNumber],
    queryFn: () => casesService.getByCaseNumber(caseNumber!),
    enabled: !!caseNumber,
  });
}

export function useCaseSpecimens(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-specimens", caseId],
    queryFn: () => casesService.getSpecimens(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseBlocks(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-blocks", caseId],
    queryFn: () => casesService.getBlocks(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseSlides(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-slides", caseId],
    queryFn: () => casesService.getSlides(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseStats(filters: CaseFilters = {}) {
  const statsFilters = getCaseStatsFilters(filters);
  return useQuery<CaseStats>({
    queryKey: ["case-stats", statsFilters],
    queryFn: () => casesService.getCaseStats(statsFilters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useDashboardStats() {
  return useQuery<DashboardStats>({
    queryKey: ["dashboard-stats"],
    queryFn: () => casesService.getDashboardStats(),
    refetchInterval: 60000, // Refresh every minute
  });
}

export function useCaseStatusHistory(caseId: string | undefined) {
  return useQuery<CaseStatusHistoryItem[]>({
    queryKey: ["case-status-history", caseId],
    queryFn: () => casesService.getStatusHistory(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseFiles(caseId: string | undefined) {
  return useQuery<CaseFile[]>({
    queryKey: ["case-files", caseId],
    queryFn: () => casesService.getFiles(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseErrorReports(caseId: string | undefined) {
  return useQuery<CaseErrorReport[]>({
    queryKey: ["case-errors", caseId],
    queryFn: () => casesService.getErrorReports(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseEmails(caseId: string | undefined) {
  return useQuery<CaseEmail[]>({
    queryKey: ["case-emails", caseId],
    queryFn: () => casesService.getEmails(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseCommunications(caseId: string | undefined) {
  return useQuery<UnifiedCommunication[]>({
    queryKey: ["case-communications", caseId],
    queryFn: () => casesService.getCommunications(caseId!),
    enabled: !!caseId,
  });
}

export function useAddCommunicationLog(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      type: "call" | "email";
      contactPerson?: string;
      subject?: string;
      notes: string;
    }) => casesService.addCommunicationLog(caseId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["case-communications", caseId],
      });
    },
  });
}

/**
 * Fetch prior cases for the same patient (matched by MRN or Name+DOB).
 * Patient matching is handled server-side.
 */
export function usePriorCases(caseId: string | undefined) {
  return useQuery<PaginatedResponse<Case>>({
    queryKey: ["prior-cases", caseId],
    queryFn: () => casesService.getPriorCases(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseNotes(caseId: string | undefined) {
  return useQuery<CaseNote[]>({
    queryKey: ["case-notes", caseId],
    queryFn: () => casesService.getNotes(caseId!),
    enabled: !!caseId,
  });
}

/**
 * Get additional stains (child cases) for a parent case.
 * Returns both active and completed child cases with workflow status.
 */
export function useCaseAdditionalStains(caseId: string | undefined) {
  return useQuery<AdditionalStainInfo[]>({
    queryKey: ["case-additional-stains", caseId],
    queryFn: () => casesService.getAdditionalStains(caseId!),
    enabled: !!caseId,
  });
}

export function useCaseQaReviews(caseId: string | undefined) {
  return useQuery<CaseQaReview[]>({
    queryKey: ["case-qa-reviews", caseId],
    queryFn: () => casesService.getQaReviews(caseId!),
    enabled: !!caseId,
  });
}

export function useCreateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCaseDto) => casesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      notifications.show({
        title: "Success",
        message: "Case created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to create case",
        color: "red",
      });
    },
  });
}

export function useUpdateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCaseDto }) =>
      casesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case", id] });
      notifications.show({
        title: "Success",
        message: "Case updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to update case",
        color: "red",
      });
    },
  });
}

export function useUpdateCaseStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      statusId,
      notes,
    }: {
      id: string;
      statusId: string;
      notes?: string;
    }) => casesService.updateStatus(id, statusId, notes),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case", id] });
      notifications.show({
        title: "Success",
        message: "Case status updated",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to update status",
        color: "red",
      });
    },
  });
}

export function useToggleCaseBlocked() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => casesService.toggleBlocked(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case", id] });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.response?.data?.message || "Failed to toggle blocked status",
        color: "red",
      });
    },
  });
}

export function useDeleteCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => casesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      notifications.show({
        title: "Success",
        message: "Case deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to delete case",
        color: "red",
      });
    },
  });
}

/**
 * Fetch missed cases (empty draft cases that were accidentally created).
 */
export function useMissedCases(
  page = 1,
  limit = 20,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: ["missed-cases", page, limit],
    queryFn: () => casesService.getAll({ onlyEmptyDrafts: true }, page, limit),
    enabled: options.enabled !== false,
  });
}

/**
 * Discard an empty draft case (guarded — only truly empty drafts).
 */
export function useDiscardDraftCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => casesService.discardDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["missed-cases"] });
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case-stats"] });
      notifications.show({
        title: "Case discarded",
        message: "Empty draft case has been removed",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.response?.data?.message || "Failed to discard draft case",
        color: "red",
      });
    },
  });
}

/**
 * Hook for generating cassette labels for printing
 *
 * NOTE: Actual Thermo PrintMate printer integration is mocked for now.
 * Labels are generated with correct format for future integration.
 */
export function useGenerateCassetteLabels() {
  return useMutation({
    mutationFn: ({
      caseId,
      request,
    }: {
      caseId: string;
      request: GenerateCassetteLabelsRequest;
    }) => casesService.generateCassetteLabels(caseId, request),
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.response?.data?.message || "Failed to generate cassette labels",
        color: "red",
      });
    },
  });
}

/**
 * Hook for generating accessioning-time sticker labels. Resolves the
 * specimen/block/slide numbering for completed slides before they are
 * persisted, so labels can be printed during accessioning.
 */
export function useGenerateStickerLabels() {
  return useMutation({
    mutationFn: ({
      caseId,
      request,
    }: {
      caseId: string;
      request: GenerateStickerLabelsRequest;
    }) => casesService.generateStickerLabels(caseId, request),
  });
}
